import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { CompanyRelationship } from "@/utils/companyRelationship";

export interface CompanyData {
  id: string;
  name: string;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  assignee?: string | null;
  notes?: string | null;
  vat_number?: string | null;
  website_url?: string | null;
  logo_url?: string | null;
  default_language?: string | null;
  relationship?: CompanyRelationship | null;
  custom_fields?: Record<string, any>;
}

// Update the interface to accept either a refresh function or an object containing it
type RefreshFunctionParam = (() => void) | { fetchCompanies: () => void } | { getCompanies: () => void };

export const useOrganizationManagement = (refreshData: RefreshFunctionParam) => {
  // NOTE: History tracking is now handled automatically by database triggers
  // No need for useHistoryOperations here
  
  // Extract the refresh function regardless of the format it was passed in
  const getRefreshFunction = () => {
    if (typeof refreshData === 'function') {
      return refreshData;
    } else if ('fetchCompanies' in refreshData) {
      return refreshData.fetchCompanies;
    } else if ('getCompanies' in refreshData) {
      return refreshData.getCompanies;
    }
    return () => console.warn('No valid refresh function provided');
  };
  
  const handleEditCompany = async (companyData: CompanyData, customFields?: Record<string, any>) => {
    try {
      //console.log("Updating company:", companyData);
      
      // First get the current company data to compare for history logging
      const { data: currentCompany, error: fetchError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyData.id)
        .single();
        
      if (fetchError) {
        console.error("Error fetching current company data:", fetchError);
        throw fetchError;
      }
      
      // Create an update object with only the fields that are provided
      const updateData: any = {};
        // Always include name as it's required
      updateData.name = companyData.name;
      
      // Add optional fields if they exist
      if (companyData.street !== undefined) updateData.street = companyData.street;
      if (companyData.postal_code !== undefined) updateData.postal_code = companyData.postal_code;
      if (companyData.city !== undefined) updateData.city = companyData.city;
      if (companyData.country !== undefined) updateData.country = companyData.country;
      if (companyData.assignee !== undefined) updateData.assignee = companyData.assignee;
      if (companyData.vat_number !== undefined) updateData.vat_number = companyData.vat_number;
      if (companyData.website_url !== undefined) updateData.website_url = companyData.website_url;
      if (companyData.logo_url !== undefined) updateData.logo_url = companyData.logo_url;
      if (companyData.default_language !== undefined) updateData.default_language = companyData.default_language;
      if (companyData.relationship !== undefined) updateData.relationship = companyData.relationship;
      
      // Add custom fields if provided
      if (customFields && Object.keys(customFields).length > 0) {
        updateData.custom_fields = customFields;
      }
      
      const { error } = await supabase
        .from('companies')
        .update(updateData)
        .eq('id', companyData.id);
      
      if (error) {
        console.error("Error updating company:", error);
        throw error;
      }
      
      // NOTE: History tracking is now handled automatically by database triggers
      // No manual history logging needed here
      
      toast({
        title: "Success",
        description: "Company updated successfully",
      });
      
      getRefreshFunction()(); // Call the refresh function
      return true;
    } catch (error: any) {
      console.error("Error updating company:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update company",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleAddCompany = async (name: string) => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .insert([{ name }])
        .select('id, name')
        .single();
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Company added successfully",
      });
      
      getRefreshFunction()(); // Call the refresh function
      return data;
    } catch (error: any) {
      console.error("Error adding company:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add company",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    try {
      // First get company data for history logging
      const { data: company, error: fetchError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
        
      if (fetchError) {
        console.error("Error fetching company data:", fetchError);
        throw fetchError;
      }
      
      // Delete the company
      const { error } = await supabase
        .from('companies')
        .delete()
        .eq('id', companyId);
    
      if (error) {
        console.error("Error deleting company:", error);
        throw error;
      }
      
      /* // Log deletion to history
      if (company) {
        await logHistory({
          entityId: companyId,
          entityType: "company",
          action: "deleted",
          description: `Company "${company.name}" was deleted`
        });
      } */
      
      toast({
        title: "Success",
        description: "Company deleted successfully",
      });
      
      getRefreshFunction()();
      return true;
    } catch (error: any) {
      console.error("Error deleting company:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete company",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    handleEditCompany,
    handleAddCompany,
    handleDeleteCompany
  };
};
